import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useLanguage } from "../LanguageContext";
import { useLocation } from "react-router-dom";

export default function SocialMediaMarketing() {
  const { t, currentLanguage } = useLanguage();
  const location = useLocation();
  // Single state to track which case study is expanded across all sections
  const [expandedCaseStudy, setExpandedCaseStudy] = useState<string | null>(null);
  // State to show "Coming Soon" message temporarily
  const [showComingSoon, setShowComingSoon] = useState(false);

  // TEMPORARY FLAG: Set to true to enable case studies, false to show "coming soon"
  const CASE_STUDIES_ENABLED = true; // Easy toggle - change to true to enable case studies

  // Scroll to KOL section if coming from homepage
  useEffect(() => {
    // Check if we should scroll to KOL section
    if (location.hash === '#kol-marketing' || location.state?.scrollToKol) {
      setTimeout(() => {
        const kolSection = document.getElementById('kol-marketing');
        if (kolSection) {
          const navbarHeight = 112;
          const elementPosition = kolSection.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location]);

  // Close expansion zone when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (expandedCaseStudy) {
        const expansionArea = document.querySelector('.expansion-area');
        const triggerElements = document.querySelectorAll('[data-case-trigger]');
        
        // Check if click is outside expansion area and not on a trigger element
        let isClickOnTrigger = false;
        
        triggerElements.forEach(trigger => {
          if (trigger.contains(event.target as Node)) {
            isClickOnTrigger = true;
          }
        });
        
        if (expansionArea && !expansionArea.contains(event.target as Node) && !isClickOnTrigger) {
          // Simply close the expansion
          setExpandedCaseStudy(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expandedCaseStudy]);

  // Case studies data for each industry
  const caseStudiesData = {
    "Food & Beverage": {
      title: "Food & Beverage Case Studies",
      description: "Successfully launched and promoted F&B brands across Southeast Asia",
      campaigns: [
        {
          client: "Premium Restaurant Chain",
          campaign: "Grand Opening Campaign",
          platform: "WeChat, Weibo, Xiaohongshu",
          results: "300% increase in reservations, 50K+ new followers",
          duration: "3 months"
        },
        {
          client: "Local Coffee Brand",
          campaign: "Seasonal Menu Launch",
          platform: "Douyin, Weibo",
          results: "2M+ video views, 25% sales increase",
          duration: "2 months"
        },
        {
          client: "Bubble Tea Chain",
          campaign: "Student Discount Campaign",
          platform: "Xiaohongshu, WeChat",
          results: "150K+ engagement, 40% new customer acquisition",
          duration: "1 month"
        }
      ],
      testimonial: "Z Creative Partners helped us reach Chinese tourists effectively. Our restaurant bookings from Chinese customers increased by 300% within the first quarter.",
      clientName: "Restaurant Manager, Premium Dining Group"
    },
    "Hotels & Tourism": {
      title: "Hotels & Tourism Case Studies", 
      description: "Boosted hotel bookings and tourism experiences for Chinese travelers",
      campaigns: [
        {
          client: "Luxury Beach Resort",
          campaign: "Chinese New Year Package",
          platform: "Weibo, WeChat, Ctrip",
          results: "85% booking rate, 500+ direct bookings",
          duration: "2 months"
        },
        {
          client: "City Tour Operator",
          campaign: "Cultural Heritage Tours",
          platform: "Xiaohongshu, Douyin",
          results: "1.5M+ views, 200% tour bookings increase",
          duration: "4 months"
        },
        {
          client: "Boutique Hotel Chain",
          campaign: "Instagram-worthy Stays",
          platform: "Xiaohongshu, WeChat",
          results: "60% occupancy increase, 100K+ social shares",
          duration: "6 months"
        }
      ],
      testimonial: "Our partnership with Z Creative Partners transformed our Chinese market presence. We went from 20% to 80% Chinese guest occupancy.",
      clientName: "Marketing Director, Luxury Resorts SEA"
    },
    "Retail": {
      title: "Retail Case Studies",
      description: "Drove sales and brand awareness for retail brands targeting Chinese consumers",
      campaigns: [
        {
          client: "Fashion Accessories Brand",
          campaign: "Singles Day Flash Sale",
          platform: "Tmall, Weibo, Xiaohongshu",
          results: "500% sales spike, 80K+ new customers",
          duration: "1 month"
        },
        {
          client: "Electronics Retailer",
          campaign: "Product Launch Campaign",
          platform: "Douyin, Weibo",
          results: "2.5M+ impressions, 35% conversion rate",
          duration: "3 months"
        },
        {
          client: "Local Craft Store",
          campaign: "DIY Tutorial Series",
          platform: "Xiaohongshu, WeChat",
          results: "300K+ video views, 150% online sales growth",
          duration: "4 months"
        }
      ],
      testimonial: "Z Creative Partners' strategy helped us tap into the Chinese consumer market. Our online sales increased by 400% in just six months.",
      clientName: "E-commerce Manager, Regional Retail Chain"
    },
    "Beauty & Skincare": {
      title: "Beauty & Skincare Case Studies",
      description: "Enhanced brand visibility and sales for beauty brands among Chinese consumers",
      campaigns: [
        {
          client: "Organic Skincare Brand",
          campaign: "Natural Beauty Campaign",
          platform: "Xiaohongshu, Weibo, Douyin",
          results: "1M+ video views, 250% product sales increase",
          duration: "5 months"
        },
        {
          client: "Premium Cosmetics Line",
          campaign: "Influencer Collaboration Series",
          platform: "Douyin, Xiaohongshu",
          results: "3M+ total reach, 60% brand awareness lift",
          duration: "3 months"
        },
        {
          client: "Local Spa Chain",
          campaign: "Self-care Sundays",
          platform: "WeChat, Xiaohongshu",
          results: "200% booking increase, 50K+ new members",
          duration: "2 months"
        }
      ],
      testimonial: "Working with Z Creative Partners opened up the Chinese beauty market for us. Our brand recognition among Chinese consumers grew by 300%.",
      clientName: "Brand Manager, Premium Beauty Co."
    },
    "Fashion": {
      title: "Fashion Case Studies",
      description: "Elevated fashion brands and increased sales through targeted Chinese social media campaigns",
      campaigns: [
        {
          client: "Sustainable Fashion Brand",
          campaign: "Eco-Fashion Awareness",
          platform: "Xiaohongshu, Weibo, Douyin",
          results: "800K+ impressions, 180% sales growth",
          duration: "4 months"
        },
        {
          client: "Local Designer Boutique",
          campaign: "Limited Edition Collection",
          platform: "WeChat, Xiaohongshu",
          results: "100% sell-out rate, 30K+ new followers",
          duration: "2 months"
        },
        {
          client: "Streetwear Brand",
          campaign: "Youth Culture Campaign",
          platform: "Douyin, Weibo",
          results: "2M+ video views, 300% online engagement",
          duration: "3 months"
        }
      ],
      testimonial: "Z Creative Partners understood our brand aesthetic perfectly. They helped us connect with young Chinese consumers and boosted our sales by 250%.",
      clientName: "Creative Director, Urban Fashion Label"
    },
    "KOL/KOC Marketing": {
      title: "KOL/KOC Marketing Case Studies",
      description: "Successful influencer partnerships and content creator collaborations",
      campaigns: [
        {
          client: "Luxury Watch Brand",
          campaign: "Celebrity Endorsement Campaign",
          platform: "Weibo, Xiaohongshu, Douyin",
          results: "5M+ total reach, 400% brand mention increase",
          duration: "6 months"
        },
        {
          client: "Health Supplement Brand",
          campaign: "Fitness Influencer Partnership",
          platform: "Xiaohongshu, Douyin",
          results: "2.5M+ video views, 300% product sales boost",
          duration: "4 months"
        },
        {
          client: "Travel Agency",
          campaign: "Travel Blogger Collaboration",
          platform: "Weibo, WeChat, Xiaohongshu",
          results: "1.8M+ impressions, 250% booking inquiries",
          duration: "3 months"
        }
      ],
      testimonial: "Z Creative Partners connected us with the perfect KOLs for our brand. The authenticity and reach of their influencer network exceeded our expectations.",
      clientName: "Marketing Manager, Premium Lifestyle Brands"
    },
    "Chinese-Friendly Support": {
      title: "Chinese-Friendly Support Case Studies",
      description: "Comprehensive offline support services enhancing customer experience",
      campaigns: [
        {
          client: "5-Star Hotel Chain",
          campaign: "Mandarin Customer Service",
          platform: "WeChat, Phone Support, In-person",
          results: "95% customer satisfaction, 60% repeat bookings",
          duration: "Ongoing"
        },
        {
          client: "Luxury Shopping Mall",
          campaign: "Chinese Payment Integration",
          platform: "Alipay, WeChat Pay, UnionPay",
          results: "80% payment adoption, 40% sales increase",
          duration: "2 months setup"
        },
        {
          client: "Tourism Board",
          campaign: "Chinese Guide Services",
          platform: "On-site support, WeChat assistance",
          results: "90% tour completion rate, 4.8/5 satisfaction score",
          duration: "Seasonal"
        }
      ],
      testimonial: "The offline support team made all the difference. Our Chinese guests now feel truly welcomed and understood, leading to incredible word-of-mouth referrals.",
      clientName: "Guest Relations Director, Hospitality Group"
    }
  };

  const handleCaseStudyClick = (caseStudyTitle: string) => {
    // If case studies are disabled, show "Coming Soon" message
    if (!CASE_STUDIES_ENABLED) {
      setShowComingSoon(true);
      setTimeout(() => setShowComingSoon(false), 2000); // Hide after 2 seconds
      return;
    }

    if (expandedCaseStudy === caseStudyTitle) {
      // Closing the same case study
      setExpandedCaseStudy(null);
    } else {
      // Opening a different case study
      setExpandedCaseStudy(caseStudyTitle);
      
      // Scroll to the expansion area after a short delay to allow animation to start
      setTimeout(() => {
        const expandedElement = document.querySelector(`[data-expanded="${caseStudyTitle}"]`) ||
                               document.querySelector('.expansion-area');
        if (expandedElement) {
          const headerOffset = 200;
          const elementPosition = expandedElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 150);
    }
  };

  // Render expanded case study content for Social Media Marketing industries
  const renderIndustryExpandedContent = () => {
    const industries = ["Food & Beverage", "Hotels & Tourism", "Retail", "Beauty & Skincare", "Fashion", "Spa & Wellness"];
    
    if (!expandedCaseStudy || !industries.includes(expandedCaseStudy)) {
      return null;
    }

    // Special handling for Food & Beverage - Tempura Yamaya case study
    if (expandedCaseStudy === "Food & Beverage") {
      return (
        <motion.div
          className="expansion-area relative bg-white border border-slate-200/60 rounded-xl p-8 shadow-lg mb-8"
          data-expanded={expandedCaseStudy}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Triangle pointer */}
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <div className="w-6 h-6 bg-white border-l border-t border-slate-200/60 transform rotate-45"></div>
          </div>

          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <h3 className="text-2xl lg:text-3xl text-foreground">
              {t('caseStudies.fnb.title')}
            </h3>
            <button
              onClick={() => setExpandedCaseStudy(null)}
              className="p-2 hover:bg-slate-100/50 rounded-lg transition-colors duration-200"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6L18 18" />
              </svg>
            </button>
          </div>

          {/* Divider Line after title */}
          <div className="w-full border-t border-slate-300 my-6"></div>

          {/* Tempura Yamaya Case Study */}
          <div className="space-y-8">
            {/* Header Image */}
            <div className="w-full flex justify-center mb-6">
              <img 
                src="/images/case_studies/fnb/case_1_logo.png" 
                alt="Tempura Yamaya Case Study"
                className="max-w-full h-auto rounded-lg"
                style={{ maxHeight: '150px' }}
              />
            </div>

            {/* Three Column Layout */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {/* Background */}
              <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  {t('caseStudies.fnb.tempuraYamaya.backgroundTitle')}
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {t('caseStudies.fnb.tempuraYamaya.background')}
                </p>
              </div>

              {/* Our Solution */}
              <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  {t('caseStudies.fnb.tempuraYamaya.solutionTitle')}
                </h3>
                <p className="text-sm text-slate-700 mb-3">
                  {t('caseStudies.fnb.tempuraYamaya.solution.title')}
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.fnb.tempuraYamaya.solution.point1')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.fnb.tempuraYamaya.solution.point2')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.fnb.tempuraYamaya.solution.point3')}</span>
                  </li>
                </ul>
              </div>

              {/* Impact Highlights */}
              <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  {t('caseStudies.fnb.tempuraYamaya.impactTitle')}
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.fnb.tempuraYamaya.impact.point1')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.fnb.tempuraYamaya.impact.point2')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.fnb.tempuraYamaya.impact.point3')}</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* SNS Screenshots Section */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-foreground text-center">
                {t('caseStudies.fnb.tempuraYamaya.snsTitle')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="/images/case_studies/fnb/case_1_pic_1.png" 
                  alt="Tempura Yamaya SNS Post 1"
                  className="w-full h-auto rounded-lg border border-slate-200/60 shadow-sm"
                />
                <img 
                  src="/images/case_studies/fnb/case_1_pic_2.png" 
                  alt="Tempura Yamaya SNS Post 2"
                  className="w-full h-auto rounded-lg border border-slate-200/60 shadow-sm"
                />
                <img 
                  src="/images/case_studies/fnb/case_1_pic_3.png" 
                  alt="Tempura Yamaya SNS Post 3"
                  className="w-full h-auto rounded-lg border border-slate-200/60 shadow-sm"
                />
                <img 
                  src="/images/case_studies/fnb/case_1_pic_4.png" 
                  alt="Tempura Yamaya SNS Post 4"
                  className="w-full h-auto rounded-lg border border-slate-200/60 shadow-sm"
                />
              </div>
            </div>

            {/* Divider Line - Separates different use cases */}
            <div className="w-full border-t border-slate-300 my-8"></div>

            {/* OmakaseDon by Teppen Case Study */}
            <div className="space-y-8">
              {/* Header Image */}
              <div className="w-full flex justify-center mb-6">
                <img 
                  src="/images/case_studies/fnb/case_2_logo.png" 
                  alt="OmakaseDon by Teppen Case Study"
                  className="max-w-full h-auto rounded-lg"
                  style={{ maxHeight: '150px' }}
                />
              </div>

              {/* Three Column Layout */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {/* Background */}
                <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-foreground mb-3">
                    {t('caseStudies.fnb.omakaseDon.backgroundTitle')}
                  </h3>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {t('caseStudies.fnb.omakaseDon.background')}
                  </p>
                </div>

                {/* Our Solution */}
                <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-foreground mb-3">
                    {t('caseStudies.fnb.omakaseDon.solutionTitle')}
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                      <span className="text-sm text-slate-700">{t('caseStudies.fnb.omakaseDon.solution.point1')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                      <span className="text-sm text-slate-700">{t('caseStudies.fnb.omakaseDon.solution.point2')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                      <span className="text-sm text-slate-700">{t('caseStudies.fnb.omakaseDon.solution.point3')}</span>
                    </li>
                  </ul>
                </div>

                {/* Impact Highlights */}
                <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-foreground mb-3">
                    {t('caseStudies.fnb.omakaseDon.impactTitle')}
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                      <span className="text-sm text-slate-700">{t('caseStudies.fnb.omakaseDon.impact.point1')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                      <span className="text-sm text-slate-700">{t('caseStudies.fnb.omakaseDon.impact.point2')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                      <span className="text-sm text-slate-700">{t('caseStudies.fnb.omakaseDon.impact.point3')}</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* SNS Screenshots Section */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-foreground text-center">
                  {t('caseStudies.fnb.omakaseDon.snsTitle')}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <img 
                    src="/images/case_studies/fnb/case_2_pic_1.png" 
                    alt="OmakaseDon SNS Post 1"
                    className="w-full h-auto rounded-lg border border-slate-200/60 shadow-sm"
                  />
                  <img 
                    src="/images/case_studies/fnb/case_2_pic_2.png" 
                    alt="OmakaseDon SNS Post 2"
                    className="w-full h-auto rounded-lg border border-slate-200/60 shadow-sm"
                  />
                  <img 
                    src="/images/case_studies/fnb/case_2_pic_3.png" 
                    alt="OmakaseDon SNS Post 3"
                    className="w-full h-auto rounded-lg border border-slate-200/60 shadow-sm"
                  />
                  <img 
                    src="/images/case_studies/fnb/case_2_pic_4.png" 
                    alt="OmakaseDon SNS Post 4"
                    className="w-full h-auto rounded-lg border border-slate-200/60 shadow-sm"
                  />
                </div>
              </div>
            </div>

            {/* Divider Line - End of OmakaseDon case study */}
            <div className="w-full border-t border-slate-300 my-8"></div>

            {/* CTA */}
            <div className="text-center pt-6">
              <motion.button
                className="bg-slate-800 text-white px-8 py-3 rounded-lg hover:bg-slate-700 transition-all duration-300 hover:shadow-lg relative overflow-hidden group cursor-pointer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  const contactSection = document.getElementById('contact');
                  if (contactSection) {
                    const headerOffset = 120;
                    const elementPosition = contactSection.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                    
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                }}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent transform -skew-x-12 group-hover:skew-x-12 transition-transform duration-700"></span>
                <span className="relative z-10">{t('socialMedia.startYourCampaign')}</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      );
    }

    // Special handling for Hotels & Tourism - The Rich Residence case study
    if (expandedCaseStudy === "Hotels & Tourism") {
      return (
        <motion.div
          className="expansion-area relative bg-white border border-slate-200/60 rounded-xl p-8 shadow-lg mb-8"
          data-expanded={expandedCaseStudy}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Triangle pointer */}
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <div className="w-6 h-6 bg-white border-l border-t border-slate-200/60 transform rotate-45"></div>
          </div>

          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <h3 className="text-2xl lg:text-3xl text-foreground">
              {t('caseStudies.hotel.title')}
            </h3>
            <button
              onClick={() => setExpandedCaseStudy(null)}
              className="p-2 hover:bg-slate-100/50 rounded-lg transition-colors duration-200"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6L18 18" />
              </svg>
            </button>
          </div>

          {/* Divider Line after title */}
          <div className="w-full border-t border-slate-300 my-6"></div>

          {/* Case 1 - Shangri-La Hotel, Chiang Mai */}
          <div className="space-y-8">
            {/* Header Image */}
            <div className="w-full flex justify-center mb-6">
              <img
                src="/images/case_studies/hotel/case_1_logo.png"
                alt="Shangri-La Hotel, Chiang Mai Case Study"
                className="max-w-full h-auto rounded-lg"
                style={{ maxHeight: '150px' }}
              />
            </div>

            {/* Three Column Layout */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {/* Background */}
              <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  {t('common.background')}
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {t('caseStudies.hotel.shangriLa.background')}
                </p>
              </div>

              {/* Our Solution */}
              <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  {t('common.ourSolution')}
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.hotel.shangriLa.solution1')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.hotel.shangriLa.solution2')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.hotel.shangriLa.solution3')}</span>
                  </li>
                </ul>
              </div>

              {/* Impact Highlights */}
              <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  {t('common.impactHighlights')}
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.hotel.shangriLa.impact1')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.hotel.shangriLa.impact2')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.hotel.shangriLa.impact3')}</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Official Account Management in Social Media RedBook(Xiaohongshu) - Title */}
            <div className="text-center">
              <h4 className="text-xl text-foreground font-medium">
                {t('caseStudies.hotel.shangriLa.title')}
              </h4>
            </div>

            {/* Four Pictures Grid - 2x2 layout */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <img 
                src="/images/case_studies/hotel/case_1_pic_1.png" 
                alt="SNS Management 1"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <img 
                src="/images/case_studies/hotel/case_1_pic_2.png" 
                alt="SNS Management 2"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <img 
                src="/images/case_studies/hotel/case_1_pic_3.png" 
                alt="SNS Management 3"
                className="w-full h-auto rounded-lg shadow-sm"
              />
              <img 
                src="/images/case_studies/hotel/case_1_pic_4.png" 
                alt="SNS Management 4"
                className="w-full h-auto rounded-lg shadow-sm"
              />
            </div>

            {/* Divider Line - End of Shangri-La case study */}
            <div className="w-full border-t border-slate-300 my-8"></div>

            {/* Case 2 - Rich Residence */}
            <div className="space-y-8">
              {/* Header Logo */}
              <div className="w-full flex justify-center mb-6">
                <img
                  src="/images/case_studies/hotel/case_2_logo.png"
                  alt="Rich Residence Case Study"
                  className="max-w-full h-auto rounded-lg"
                  style={{ maxHeight: '150px' }}
                />
              </div>

              {/* Three Column Layout */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {/* Background */}
                <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-foreground mb-3">
                    {t('common.background')}
                  </h3>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {t('caseStudies.hotel.richResidence.background')}
                  </p>
                </div>

                {/* Our Solution */}
                <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-foreground mb-3">
                    {t('common.ourSolution')}
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                      <span className="text-sm text-slate-700">{t('caseStudies.hotel.richResidence.solution1')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                      <span className="text-sm text-slate-700">{t('caseStudies.hotel.richResidence.solution2')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                      <span className="text-sm text-slate-700">{t('caseStudies.hotel.richResidence.solution3')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                      <span className="text-sm text-slate-700">{t('caseStudies.hotel.richResidence.solution4')}</span>
                    </li>
                  </ul>
                </div>

                {/* Impact Highlights */}
                <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-foreground mb-3">
                    {t('common.impactHighlights')}
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                      <span className="text-sm text-slate-700">{t('caseStudies.hotel.richResidence.impact1')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                      <span className="text-sm text-slate-700">{t('caseStudies.hotel.richResidence.impact2')}</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                      <span className="text-sm text-slate-700">{t('caseStudies.hotel.richResidence.impact3')}</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Official Account Management in Social Media RedBook(Xiaohongshu) - Title */}
              <div className="text-center">
                <h4 className="text-xl text-foreground font-medium">
                  {t('caseStudies.hotel.richResidence.title')}
                </h4>
              </div>

              {/* Four Pictures Grid - 2x2 layout */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <img
                  src="/images/case_studies/hotel/case_2_pic_1.png"
                  alt="Rich Residence SNS Management 1"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
                <img
                  src="/images/case_studies/hotel/case_2_pic_2.png"
                  alt="Rich Residence SNS Management 2"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
                <img
                  src="/images/case_studies/hotel/case_2_pic_3.png"
                  alt="Rich Residence SNS Management 3"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
                <img
                  src="/images/case_studies/hotel/case_2_pic_4.png"
                  alt="Rich Residence SNS Management 4"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
              </div>

              {/* Divider Line - End of Rich Residence case study */}
              <div className="w-full border-t border-slate-300"></div>
            </div>

            {/* CTA */}
            <div className="text-center pt-6">
              <motion.button
                className="bg-slate-800 text-white px-8 py-3 rounded-lg hover:bg-slate-700 transition-all duration-300 hover:shadow-lg relative overflow-hidden group cursor-pointer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  const contactSection = document.getElementById('contact');
                  if (contactSection) {
                    const headerOffset = 120;
                    const elementPosition = contactSection.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                    
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                }}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent transform -skew-x-12 group-hover:skew-x-12 transition-transform duration-700"></span>
                <span className="relative z-10">{t('socialMedia.startYourCampaign')}</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      );
    }

    // Special handling for Retail - Siam Takashimaya case study
    if (expandedCaseStudy === "Retail") {
      return (
        <motion.div
          className="expansion-area relative bg-white border border-slate-200/60 rounded-xl p-8 shadow-lg mb-8"
          data-expanded={expandedCaseStudy}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Triangle pointer */}
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <div className="w-6 h-6 bg-white border-l border-t border-slate-200/60 transform rotate-45"></div>
          </div>

          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <h3 className="text-2xl lg:text-3xl text-foreground">
              {t('caseStudies.retail.title')}
            </h3>
            <button
              onClick={() => setExpandedCaseStudy(null)}
              className="p-2 hover:bg-slate-100/50 rounded-lg transition-colors duration-200"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6L18 18" />
              </svg>
            </button>
          </div>

          {/* Divider Line after title */}
          <div className="w-full border-t border-slate-300 my-6"></div>

          {/* Siam Takashimaya Case Study */}
          <div className="space-y-8">
            {/* Header Image */}
            <div className="w-full flex justify-center mb-6">
              <img 
                src="/images/case_studies/retail/case_1_logo.png" 
                alt="Siam Takashimaya Case Study"
                className="max-w-full h-auto rounded-lg"
                style={{ maxHeight: '150px' }}
              />
            </div>

            {/* Three Column Layout */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {/* Background */}
              <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  {t('caseStudies.retail.siamTakashimaya.backgroundTitle')}
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {t('caseStudies.retail.siamTakashimaya.background')}
                </p>
              </div>

              {/* Our Solution */}
              <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  {t('caseStudies.retail.siamTakashimaya.solutionTitle')}
                </h3>
                <p className="text-sm text-slate-700 mb-3">
                  {t('caseStudies.retail.siamTakashimaya.solution.title')}
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.retail.siamTakashimaya.solution.point1')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.retail.siamTakashimaya.solution.point2')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-600 mr-2 flex-shrink-0">•</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.retail.siamTakashimaya.solution.point3')}</span>
                  </li>
                </ul>
              </div>

              {/* Impact Highlights */}
              <div className="bg-white border border-slate-200/60 rounded-lg p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  {t('caseStudies.retail.siamTakashimaya.impactTitle')}
                </h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.retail.siamTakashimaya.impact.point1')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.retail.siamTakashimaya.impact.point2')}</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2 flex-shrink-0">✓</span>
                    <span className="text-sm text-slate-700">{t('caseStudies.retail.siamTakashimaya.impact.point3')}</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Official Account Management in Social Media WeChat - Title */}
            <div className="text-center">
              <h4 className="text-xl text-foreground font-medium">
                {t('caseStudies.retail.siamTakashimaya.snsTitle')}
              </h4>
            </div>

            {/* Six Pictures Grid - 4 in first row, 2 in second row */}
            <div className="space-y-6 mb-8">
              {/* First row - 4 pictures */}
              <div className="grid grid-cols-4 gap-4">
                <img 
                  src="/images/case_studies/retail/case_1_pic_1.png" 
                  alt="WeChat Management 1"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
                <img 
                  src="/images/case_studies/retail/case_1_pic_2.png" 
                  alt="WeChat Management 2"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
                <img 
                  src="/images/case_studies/retail/case_1_pic_3.png" 
                  alt="WeChat Management 3"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
                <img 
                  src="/images/case_studies/retail/case_1_pic_4.png" 
                  alt="WeChat Management 4"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
              </div>
              
              {/* Official Account Management in Social Media RedNote - Title */}
              <div className="text-center">
                <h4 className="text-xl text-foreground font-medium">
                  {t('caseStudies.retail.siamTakashimaya.snsRedNoteTitle')}
                </h4>
              </div>
              
              {/* Second row - 2 pictures */}
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="/images/case_studies/retail/case_1_pic_5.png" 
                  alt="WeChat Management 5"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
                <img 
                  src="/images/case_studies/retail/case_1_pic_6.png" 
                  alt="WeChat Management 6"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
              </div>
            </div>

            {/* Divider Line - End of Siam Takashimaya case study */}
            <div className="w-full border-t border-slate-300 my-8"></div>

            {/* Case 2 - Logo and pictures only */}
            <div className="space-y-6">
              {/* Case 2 Logo */}
              <div className="w-full flex justify-center">
                <img 
                  src="/images/case_studies/retail/case_2_logo.png" 
                  alt="Retail Case Study 2"
                  className="max-w-full h-auto rounded-lg"
                  style={{ maxHeight: '150px' }}
                />
              </div>

              {/* Official Account Management in Social Media RedBook(Xiaohongshu) - Title for Case 2 */}
              <div className="text-center">
                <h4 className="text-xl text-foreground font-medium">
                  {t('caseStudies.retail.case2.snsRedNoteTitle')}
                </h4>
              </div>

              {/* Case 2 - Four pictures in 2x2 grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="overflow-hidden rounded-lg shadow-sm">
                  <img 
                    src="/images/case_studies/retail/case_2_pic_1.png" 
                    alt="Retail Case 2 Image 1"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="overflow-hidden rounded-lg shadow-sm">
                  <img 
                    src="/images/case_studies/retail/case_2_pic_2.png" 
                    alt="Retail Case 2 Image 2"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="overflow-hidden rounded-lg shadow-sm">
                  <img 
                    src="/images/case_studies/retail/case_2_pic_3.png" 
                    alt="Retail Case 2 Image 3"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="overflow-hidden rounded-lg shadow-sm">
                  <img 
                    src="/images/case_studies/retail/case_2_pic_4.png" 
                    alt="Retail Case 2 Image 4"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Divider Line - End of case 2 */}
              <div className="w-full border-t border-slate-300"></div>
            </div>

            {/* CTA */}
            <div className="text-center pt-6">
              <motion.button
                className="bg-slate-800 text-white px-8 py-3 rounded-lg hover:bg-slate-700 transition-all duration-300 hover:shadow-lg relative overflow-hidden group cursor-pointer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  const contactSection = document.getElementById('contact');
                  if (contactSection) {
                    const headerOffset = 120;
                    const elementPosition = contactSection.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                    
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                }}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent transform -skew-x-12 group-hover:skew-x-12 transition-transform duration-700"></span>
                <span className="relative z-10">{t('socialMedia.startYourCampaign')}</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      );
    }

    // Special handling for Beauty & Skincare
    if (expandedCaseStudy === "Beauty & Skincare") {
      return (
        <motion.div
          className="expansion-area relative bg-white border border-slate-200/60 rounded-xl p-8 shadow-lg mb-8"
          data-expanded={expandedCaseStudy}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Triangle pointer */}
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <div className="w-6 h-6 bg-white border-l border-t border-slate-200/60 transform rotate-45"></div>
          </div>

          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <h3 className="text-2xl lg:text-3xl text-foreground">
              {t('caseStudies.beautyAndSkincare.title')}
            </h3>
            <button
              onClick={() => setExpandedCaseStudy(null)}
              className="p-2 hover:bg-slate-100/50 rounded-lg transition-colors duration-200"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6L18 18" />
              </svg>
            </button>
          </div>

          {/* Divider Line after title */}
          <div className="w-full border-t border-slate-300 my-6"></div>

          {/* Case Studies */}
          <div className="space-y-6">
            {/* Case 1 */}
            <div className="space-y-6">
              {/* Case 1 Logo */}
              <div className="w-full flex justify-center">
                <img 
                  src="/images/case_studies/cosmetic/case_1_logo.png" 
                  alt="Beauty & Skincare Case Study 1"
                  className="max-w-full h-auto rounded-lg"
                  style={{ maxHeight: '150px' }}
                />
              </div>

              {/* Official Account Management in Social Media RedBook(Xiaohongshu) - Title for Case 1 */}
              <div className="text-center">
                <h4 className="text-xl text-foreground font-medium">
                  {t('caseStudies.beautyAndSkincare.case1.snsRedNoteTitle')}
                </h4>
              </div>

              {/* Case 1 - Two pictures side by side filling full width */}
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="/images/case_studies/cosmetic/case_1_pic_1.png"
                  alt="Beauty Case 1 Image 1"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
                <img
                  src="/images/case_studies/cosmetic/case_1_pic_2.png"
                  alt="Beauty Case 1 Image 2"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
              </div>
            </div>

            {/* Divider Line - End of case 1 */}
            <div className="w-full border-t border-slate-300 my-8"></div>

            {/* Case 2 */}
            <div className="space-y-6">
              {/* Case 2 Logo */}
              <div className="w-full flex justify-center">
                <img 
                  src="/images/case_studies/cosmetic/case_2_logo.png" 
                  alt="Beauty & Skincare Case Study 2"
                  className="max-w-full h-auto rounded-lg"
                  style={{ maxHeight: '150px' }}
                />
              </div>

              {/* Official Account Management in Social Media WeChat - Title for Case 2 */}
              <div className="text-center">
                <h4 className="text-xl text-foreground font-medium">
                  {t('caseStudies.beautyAndSkincare.case2.title')}
                </h4>
              </div>

              {/* Case 2 - Four pictures in a row with same height */}
              <div className="grid grid-cols-4 gap-4">
                <div className="overflow-hidden rounded-lg shadow-sm">
                  <img
                    src="/images/case_studies/cosmetic/case_2_pic_1.png"
                    alt="Beauty Case 2 Image 1"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="overflow-hidden rounded-lg shadow-sm">
                  <img
                    src="/images/case_studies/cosmetic/case_2_pic_2.png"
                    alt="Beauty Case 2 Image 2"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="overflow-hidden rounded-lg shadow-sm">
                  <img
                    src="/images/case_studies/cosmetic/case_2_pic_3.png"
                    alt="Beauty Case 2 Image 3"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="overflow-hidden rounded-lg shadow-sm">
                  <img
                    src="/images/case_studies/cosmetic/case_2_pic_4.png"
                    alt="Beauty Case 2 Image 4"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Divider Line - End of case 2 */}
            <div className="w-full border-t border-slate-300"></div>
          </div>

          {/* CTA */}
          <div className="text-center pt-6">
            <motion.button
              className="bg-slate-800 text-white px-8 py-3 rounded-lg hover:bg-slate-700 transition-all duration-300 hover:shadow-lg relative overflow-hidden group cursor-pointer"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                const contactSection = document.getElementById('contact');
                if (contactSection) {
                  const headerOffset = 120;
                  const elementPosition = contactSection.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                  
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }
              }}
            >
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent transform -skew-x-12 group-hover:skew-x-12 transition-transform duration-700"></span>
              <span className="relative z-10">{t('socialMedia.startYourCampaign')}</span>
            </motion.button>
          </div>
        </motion.div>
      );
    }

    // Special handling for Fashion
    if (expandedCaseStudy === "Fashion") {
      return (
        <motion.div
          className="expansion-area relative bg-white border border-slate-200/60 rounded-xl p-8 shadow-lg mb-8"
          data-expanded={expandedCaseStudy}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Triangle pointer */}
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <div className="w-6 h-6 bg-white border-l border-t border-slate-200/60 transform rotate-45"></div>
          </div>

          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <h3 className="text-2xl lg:text-3xl text-foreground">
              {t('caseStudies.fashion.title')}
            </h3>
            <button
              onClick={() => setExpandedCaseStudy(null)}
              className="p-2 hover:bg-slate-100/50 rounded-lg transition-colors duration-200"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6L18 18" />
              </svg>
            </button>
          </div>

          {/* Divider Line after title */}
          <div className="w-full border-t border-slate-300 my-6"></div>

          {/* Case 1 - Logo and pictures only */}
          <div className="space-y-6">
            {/* Case 1 Logo */}
            <div className="w-full flex justify-center">
              <img 
                src="/images/case_studies/fashion/case_1_logo.png" 
                alt="Fashion Case Study"
                className="max-w-full h-auto rounded-lg"
                style={{ maxHeight: '150px' }}
              />
            </div>

            {/* Official Account Management in Social Media RedBook(Xiaohongshu) - Title */}
            <div className="text-center">
              <h4 className="text-xl text-foreground font-medium">
                {t('caseStudies.fashion.case1.snsRedNoteTitle')}
              </h4>
            </div>

            {/* Four pictures in 2x2 grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="overflow-hidden rounded-lg shadow-sm">
                <img 
                  src="/images/case_studies/fashion/case_1_pic_1.png" 
                  alt="Fashion Case Image 1"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="overflow-hidden rounded-lg shadow-sm">
                <img 
                  src="/images/case_studies/fashion/case_1_pic_2.png" 
                  alt="Fashion Case Image 2"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="overflow-hidden rounded-lg shadow-sm">
                <img 
                  src="/images/case_studies/fashion/case_1_pic_3.png" 
                  alt="Fashion Case Image 3"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="overflow-hidden rounded-lg shadow-sm">
                <img 
                  src="/images/case_studies/fashion/case_1_pic_4.png" 
                  alt="Fashion Case Image 4"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Divider Line - End of case */}
            <div className="w-full border-t border-slate-300"></div>
          </div>

          {/* CTA */}
          <div className="text-center pt-6">
            <motion.button
              className="bg-slate-800 text-white px-8 py-3 rounded-lg hover:bg-slate-700 transition-all duration-300 hover:shadow-lg relative overflow-hidden group cursor-pointer"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                const contactSection = document.getElementById('contact');
                if (contactSection) {
                  const headerOffset = 120;
                  const elementPosition = contactSection.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                  
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }
              }}
            >
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent transform -skew-x-12 group-hover:skew-x-12 transition-transform duration-700"></span>
              <span className="relative z-10">{t('socialMedia.startYourCampaign')}</span>
            </motion.button>
          </div>
        </motion.div>
      );
    }

    // Special handling for Spa & Wellness
    if (expandedCaseStudy === "Spa & Wellness") {
      return (
        <motion.div
          className="expansion-area relative bg-white border border-slate-200/60 rounded-xl p-8 shadow-lg mb-8"
          data-expanded={expandedCaseStudy}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Triangle pointer */}
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <div className="w-6 h-6 bg-white border-l border-t border-slate-200/60 transform rotate-45"></div>
          </div>

          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <h3 className="text-2xl lg:text-3xl text-foreground">
              {t('caseStudies.spaWellness.title')}
            </h3>
            <button
              onClick={() => setExpandedCaseStudy(null)}
              className="p-2 hover:bg-slate-100/50 rounded-lg transition-colors duration-200"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6L18 18" />
              </svg>
            </button>
          </div>

          {/* Divider Line after title */}
          <div className="w-full border-t border-slate-300 my-6"></div>

          {/* Case 1 - Logo and pictures only */}
          <div className="space-y-6">
            {/* Case 1 Logo */}
            <div className="w-full flex justify-center">
              <img 
                src="/images/case_studies/spa_wellness/case_1_logo.png" 
                alt="Spa & Wellness Case Study"
                className="max-w-full h-auto rounded-lg"
                style={{ maxHeight: '150px' }}
              />
            </div>

            {/* Official Account Management in Social Media RedBook(Xiaohongshu) - Title */}
            <div className="text-center">
              <h4 className="text-xl text-foreground font-medium">
                {t('caseStudies.spaWellness.case1.snsRedNoteTitle')}
              </h4>
            </div>

            {/* Two pictures side by side */}
            <div className="flex gap-6 items-center justify-center">
              <div className="h-96 overflow-hidden rounded-lg shadow-sm">
                <img
                  src="/images/case_studies/spa_wellness/case_1_pic_1.png"
                  alt="Spa & Wellness Case Image 1"
                  className="h-full w-auto object-contain"
                />
              </div>
              <div className="h-96 overflow-hidden rounded-lg shadow-sm">
                <img
                  src="/images/case_studies/spa_wellness/case_1_pic_2.png"
                  alt="Spa & Wellness Case Image 2"
                  className="h-full w-auto object-contain"
                />
              </div>
            </div>

            {/* Divider Line - End of case 1 */}
            <div className="w-full border-t border-slate-300 my-8"></div>

            {/* Case 2 */}
            <div className="space-y-6">
              {/* Case 2 Logo */}
              <div className="w-full flex justify-center">
                <img
                  src="/images/case_studies/spa_wellness/case_2_logo.png"
                  alt="Spa & Wellness Case Study 2"
                  className="max-w-full h-auto rounded-lg"
                  style={{ maxHeight: '150px' }}
                />
              </div>

              {/* Official Account Management in Social Media RedBook(Xiaohongshu) - Title */}
              <div className="text-center">
                <h4 className="text-xl text-foreground font-medium">
                  {t('caseStudies.spaWellness.case2.title')}
                </h4>
              </div>

              {/* Four pictures in 2x2 grid */}
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="/images/case_studies/spa_wellness/case_2_pic_1.png"
                  alt="Spa & Wellness Case 2 Image 1"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
                <img
                  src="/images/case_studies/spa_wellness/case_2_pic_2.png"
                  alt="Spa & Wellness Case 2 Image 2"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
                <img
                  src="/images/case_studies/spa_wellness/case_2_pic_3.png"
                  alt="Spa & Wellness Case 2 Image 3"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
                <img
                  src="/images/case_studies/spa_wellness/case_2_pic_4.png"
                  alt="Spa & Wellness Case 2 Image 4"
                  className="w-full h-auto rounded-lg shadow-sm"
                />
              </div>

              {/* Divider Line - End of case 2 */}
              <div className="w-full border-t border-slate-300"></div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center pt-6">
            <motion.button
              className="bg-slate-800 text-white px-8 py-3 rounded-lg hover:bg-slate-700 transition-all duration-300 hover:shadow-lg relative overflow-hidden group cursor-pointer"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                const contactSection = document.getElementById('contact');
                if (contactSection) {
                  const headerOffset = 120;
                  const elementPosition = contactSection.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                  
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                }
              }}
            >
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent transform -skew-x-12 group-hover:skew-x-12 transition-transform duration-700"></span>
              <span className="relative z-10">{t('socialMedia.startYourCampaign')}</span>
            </motion.button>
          </div>
        </motion.div>
      );
    }

    // Default handling for other industries (keep existing code)
    const industryData = caseStudiesData[expandedCaseStudy as keyof typeof caseStudiesData];
    
    if (!industryData) {
      return null;
    }

    return (
      <motion.div
        className="expansion-area relative bg-white border border-slate-200/60 rounded-xl p-8 shadow-lg mb-8"
        data-expanded={expandedCaseStudy}
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Triangle pointer */}
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className="w-6 h-6 bg-white border-l border-t border-slate-200/60 transform rotate-45"></div>
        </div>

        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-2xl lg:text-3xl text-foreground mb-2">
              {industryData.title}
            </h3>
            <p className="text-muted-foreground">
              {industryData.description}
            </p>
          </div>
          <button
            onClick={() => setExpandedCaseStudy(null)}
            className="p-2 hover:bg-slate-100/50 rounded-lg transition-colors duration-200"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6L18 18" />
            </svg>
          </button>
        </div>

        {/* Campaigns Grid */}
        <div className="space-y-6 mb-8">
          <h4 className="text-xl text-foreground">Campaign Results</h4>
          <div className="grid gap-6">
            {industryData.campaigns.map((campaign: any, index: number) => (
              <motion.div
                key={index}
                className="bg-gradient-to-r from-slate-50/50 to-cyan-50/30 border border-slate-200/60 rounded-lg p-6 hover:shadow-md transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <h5 className="text-lg text-foreground">{campaign.client}</h5>
                    <p className="text-slate-600">{campaign.campaign}</p>
                    <div className="flex flex-wrap gap-2">
                      {campaign.platform.split(', ').map((platform: string, idx: number) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-cyan-400/10 text-cyan-700 rounded-full text-sm"
                        >
                          {platform}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="p-4 bg-white/60 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Results</p>
                      <p className="text-slate-800">{campaign.results}</p>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Duration</span>
                      <span className="text-sm text-slate-800">{campaign.duration}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Testimonial */}
        <motion.div
          className="bg-gradient-to-br from-slate-800/5 to-cyan-400/5 border border-slate-200/60 rounded-xl p-8 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="flex items-start space-x-4">
            <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-full flex-shrink-0 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z"/>
              </svg>
            </div>
            <div className="flex-1">
              <blockquote className="text-slate-700 italic text-lg leading-relaxed mb-4">
                "{industryData.testimonial}"
              </blockquote>
              <cite className="text-slate-600 not-italic">
                — {industryData.clientName}
              </cite>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <div className="text-center">
          <motion.button
            className="bg-slate-800 text-white px-8 py-3 rounded-lg hover:bg-slate-700 transition-all duration-300 hover:shadow-lg relative overflow-hidden group cursor-pointer"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              const contactSection = document.getElementById('contact');
              if (contactSection) {
                const headerOffset = 120;
                const elementPosition = contactSection.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                
                window.scrollTo({
                  top: offsetPosition,
                  behavior: 'smooth'
                });
              }
            }}
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent transform -skew-x-12 group-hover:skew-x-12 transition-transform duration-700"></span>
            <span className="relative z-10">{t('socialMedia.startYourCampaign')}</span>
          </motion.button>
        </div>
      </motion.div>
    );
  };

  // Render expanded case study content for KOL/KOC Marketing
  const renderKOLExpandedContent = () => {
    if (expandedCaseStudy !== "KOL/KOC Marketing") {
      return null;
    }

    // const kolData = caseStudiesData["KOL/KOC Marketing"];  // Currently unused

    return (
      <motion.div
        className="expansion-area mt-12 relative bg-white border border-slate-200/60 rounded-xl p-8 shadow-lg"
        data-expanded="KOL/KOC Marketing"
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Triangle pointer */}
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className="w-6 h-6 bg-white border-l border-t border-slate-200/60 transform rotate-45"></div>
        </div>

        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <h3 className="text-2xl lg:text-3xl text-foreground">
            {t('caseStudies.kolKoc.title')}
          </h3>
          <button
            onClick={() => setExpandedCaseStudy(null)}
            className="p-2 hover:bg-slate-100/50 rounded-lg transition-colors duration-200"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6L18 18" />
            </svg>
          </button>
        </div>

        {/* Divider Line after title */}
        <div className="w-full border-t border-slate-300 my-6"></div>

        {/* Case Studies */}
        <div className="space-y-8">
          {/* Case 1 */}
          <div className="space-y-6">
            {/* Case 1 Logo */}
            <div className="w-full flex justify-center">
              <img 
                src="/images/case_studies/kol_koc/case_1_logo.png" 
                alt="KOL/KOC Case Study 1"
                className="max-w-full h-auto rounded-lg"
                style={{ maxHeight: '150px' }}
              />
            </div>

            {/* Official Account Management in Social Media RedBook(Xiaohongshu) - Title for Case 1 */}
            <div className="text-center">
              <h4 className="text-xl text-foreground font-medium">
                {t('caseStudies.kolKoc.case1.snsRedNoteTitle')}
              </h4>
            </div>

            {/* Case 1 - Four pictures with same height in rows */}
            <div className="space-y-6">
              {/* First row - 2 pictures */}
              <div className="flex gap-6 items-center justify-center">
                <div className="h-96 overflow-hidden rounded-lg shadow-sm">
                  <img 
                    src="/images/case_studies/kol_koc/case_1_pic_1.png" 
                    alt="KOL Case 1 Image 1"
                    className="h-full w-auto object-contain"
                  />
                </div>
                <div className="h-96 overflow-hidden rounded-lg shadow-sm">
                  <img 
                    src="/images/case_studies/kol_koc/case_1_pic_2.png" 
                    alt="KOL Case 1 Image 2"
                    className="h-full w-auto object-contain"
                  />
                </div>
              </div>
              {/* Second row - 2 pictures */}
              <div className="flex gap-6 items-center justify-center">
                <div className="h-96 overflow-hidden rounded-lg shadow-sm">
                  <img 
                    src="/images/case_studies/kol_koc/case_1_pic_3.jpg" 
                    alt="KOL Case 1 Image 3"
                    className="h-full w-auto object-contain"
                  />
                </div>
                <div className="h-96 overflow-hidden rounded-lg shadow-sm">
                  <img 
                    src="/images/case_studies/kol_koc/case_1_pic_4.png" 
                    alt="KOL Case 1 Image 4"
                    className="h-full w-auto object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Divider Line - End of case 1 */}
            <div className="w-full border-t border-slate-300"></div>
          </div>

          {/* Case 2 */}
          <div className="space-y-6">
            {/* Case 2 Logo */}
            <div className="w-full flex justify-center">
              <img 
                src="/images/case_studies/kol_koc/case_2_logo.png" 
                alt="KOL/KOC Case Study 2"
                className="max-w-full h-auto rounded-lg"
                style={{ maxHeight: '150px' }}
              />
            </div>

            {/* Official Account Management in Social Media RedBook(Xiaohongshu) - Title for Case 2 */}
            <div className="text-center">
              <h4 className="text-xl text-foreground font-medium">
                {t('caseStudies.kolKoc.case2.snsRedNoteTitle')}
              </h4>
            </div>

            {/* Case 2 - Four pictures with same height in rows */}
            <div className="space-y-6">
              {/* First row - 2 pictures */}
              <div className="flex gap-6 items-center justify-center">
                <div className="h-96 overflow-hidden rounded-lg shadow-sm">
                  <img 
                    src="/images/case_studies/kol_koc/case_2_pic_1.png" 
                    alt="KOL Case 2 Image 1"
                    className="h-full w-auto object-contain"
                  />
                </div>
                <div className="h-96 overflow-hidden rounded-lg shadow-sm">
                  <img 
                    src="/images/case_studies/kol_koc/case_2_pic_2.png" 
                    alt="KOL Case 2 Image 2"
                    className="h-full w-auto object-contain"
                  />
                </div>
              </div>
              {/* Second row - 2 pictures */}
              <div className="flex gap-6 items-center justify-center">
                <div className="h-96 overflow-hidden rounded-lg shadow-sm">
                  <img 
                    src="/images/case_studies/kol_koc/case_2_pic_3.png" 
                    alt="KOL Case 2 Image 3"
                    className="h-full w-auto object-contain"
                  />
                </div>
                <div className="h-96 overflow-hidden rounded-lg shadow-sm">
                  <img 
                    src="/images/case_studies/kol_koc/case_2_pic_4.png" 
                    alt="KOL Case 2 Image 4"
                    className="h-full w-auto object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Divider Line - End of case 2 */}
            <div className="w-full border-t border-slate-300"></div>
          </div>

          {/* Case 3 - TEMPORARILY REMOVED (Update #6 - 2025-08-20) */}
          {/* Original Case 3 code commented out for easy reversal
          <div className="space-y-6">
            {/* Case 3 Logo */}
            {/* <div className="w-full flex justify-center">
              <img 
                src="/images/case_studies/kol_koc/case_3_logo.png" 
                alt="KOL/KOC Case Study 3"
                className="max-w-full h-auto rounded-lg"
                style={{ maxHeight: '150px' }}
              />
            </div> */}

            {/* Official Account Management in Social Media RedBook(Xiaohongshu) - Title for Case 3 */}
            {/* <div className="text-center">
              <h4 className="text-xl text-foreground font-medium">
                {t('caseStudies.kolKoc.case3.snsRedNoteTitle')}
              </h4>
            </div> */}

            {/* Case 3 - Four pictures with same height in rows */}
            {/* <div className="space-y-6">
              {/* First row - 2 pictures */}
              {/* <div className="flex gap-6 items-center justify-center">
                <div className="h-96 overflow-hidden rounded-lg shadow-sm">
                  <img 
                    src="/images/case_studies/kol_koc/case_3_pic_1.png" 
                    alt="KOL Case 3 Image 1"
                    className="h-full w-auto object-contain"
                  />
                </div>
                <div className="h-96 overflow-hidden rounded-lg shadow-sm">
                  <img 
                    src="/images/case_studies/kol_koc/case_3_pic_2.png" 
                    alt="KOL Case 3 Image 2"
                    className="h-full w-auto object-contain"
                  />
                </div>
              </div> */}
              {/* Second row - 2 pictures */}
              {/* <div className="flex gap-6 items-center justify-center">
                <div className="h-96 overflow-hidden rounded-lg shadow-sm">
                  <img 
                    src="/images/case_studies/kol_koc/case_3_pic_3.png" 
                    alt="KOL Case 3 Image 3"
                    className="h-full w-auto object-contain"
                  />
                </div>
                <div className="h-96 overflow-hidden rounded-lg shadow-sm">
                  <img 
                    src="/images/case_studies/kol_koc/case_3_pic_4.png" 
                    alt="KOL Case 3 Image 4"
                    className="h-full w-auto object-contain"
                  />
                </div>
              </div>
            </div> */}

            {/* Divider Line - End of case 3 */}
            {/* <div className="w-full border-t border-slate-300"></div>
          </div>
          */}
        </div>

        {/* CTA */}
        <div className="text-center mt-8">
          <motion.button
            className="bg-slate-800 text-white px-8 py-3 rounded-lg hover:bg-slate-700 transition-all duration-300 hover:shadow-lg relative overflow-hidden group cursor-pointer"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              const contactSection = document.getElementById('contact');
              if (contactSection) {
                const headerOffset = 120;
                const elementPosition = contactSection.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                
                window.scrollTo({
                  top: offsetPosition,
                  behavior: 'smooth'
                });
              }
            }}
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent transform -skew-x-12 group-hover:skew-x-12 transition-transform duration-700"></span>
            <span className="relative z-10">{t('socialMedia.startYourCampaign')}</span>
          </motion.button>
        </div>
      </motion.div>
    );
  };

  // Render expanded case study content for Chinese-Friendly Support
  const renderSupportExpandedContent = () => {
    if (expandedCaseStudy !== "Chinese-Friendly Support") {
      return null;
    }

    const supportData = caseStudiesData["Chinese-Friendly Support"];

    return (
      <motion.div
        className="expansion-area relative bg-white border border-slate-200/60 rounded-xl p-8 shadow-lg mt-12 max-w-4xl mx-auto"
        data-expanded="Chinese-Friendly Support"
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Triangle pointer */}
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className="w-6 h-6 bg-white border-l border-t border-slate-200/60 transform rotate-45"></div>
        </div>

        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-2xl lg:text-3xl text-foreground mb-2">
              {supportData.title}
            </h3>
            <p className="text-muted-foreground">
              {supportData.description}
            </p>
          </div>
          <button
            onClick={() => setExpandedCaseStudy(null)}
            className="p-2 hover:bg-slate-100/50 rounded-lg transition-colors duration-200"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6L18 18" />
            </svg>
          </button>
        </div>

        {/* Campaigns Grid */}
        <div className="space-y-6 mb-8">
          <h4 className="text-xl text-foreground">Campaign Results</h4>
          <div className="grid gap-6">
            {supportData.campaigns.map((campaign: any, index: number) => (
              <motion.div
                key={index}
                className="bg-gradient-to-r from-slate-50/50 to-cyan-50/30 border border-slate-200/60 rounded-lg p-6 hover:shadow-md transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <h5 className="text-lg text-foreground">{campaign.client}</h5>
                    <p className="text-slate-600">{campaign.campaign}</p>
                    <div className="flex flex-wrap gap-2">
                      {campaign.platform.split(', ').map((platform: string, idx: number) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-cyan-400/10 text-cyan-700 rounded-full text-sm"
                        >
                          {platform}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="p-4 bg-white/60 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Results</p>
                      <p className="text-slate-800">{campaign.results}</p>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Duration</span>
                      <span className="text-sm text-slate-800">{campaign.duration}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Testimonial */}
        <motion.div
          className="bg-gradient-to-br from-slate-800/5 to-cyan-400/5 border border-slate-200/60 rounded-xl p-8 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="flex items-start space-x-4">
            <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-full flex-shrink-0 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z"/>
              </svg>
            </div>
            <div className="flex-1">
              <blockquote className="text-slate-700 italic text-lg leading-relaxed mb-4">
                "{supportData.testimonial}"
              </blockquote>
              <cite className="text-slate-600 not-italic">
                — {supportData.clientName}
              </cite>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <div className="text-center">
          <motion.button
            className="bg-slate-800 text-white px-8 py-3 rounded-lg hover:bg-slate-700 transition-all duration-300 hover:shadow-lg relative overflow-hidden group cursor-pointer"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              const contactSection = document.getElementById('contact');
              if (contactSection) {
                const headerOffset = 120;
                const elementPosition = contactSection.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                
                window.scrollTo({
                  top: offsetPosition,
                  behavior: 'smooth'
                });
              }
            }}
          >
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent transform -skew-x-12 group-hover:skew-x-12 transition-transform duration-700"></span>
            <span className="relative z-10">{t('socialMedia.startYourCampaign')}</span>
          </motion.button>
        </div>
      </motion.div>
    );
  };

  // Industry Card Component
  const IndustryCard = ({ industry, onExpand }: { industry: any, onExpand: (title: string) => void }) => {
    const [showTooltip, setShowTooltip] = useState(false);

    const handleCardClick = () => {
      if (!CASE_STUDIES_ENABLED) {
        setShowTooltip(true);
        setTimeout(() => setShowTooltip(false), 2000);
        return;
      }
      onExpand(industry.id || industry.title);
    };

    const handleButtonClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!CASE_STUDIES_ENABLED) {
        setShowTooltip(true);
        setTimeout(() => setShowTooltip(false), 2000);
        return;
      }
      onExpand(industry.id || industry.title);
    };

    return (
      <motion.div
        className="bg-white border border-slate-200/60 rounded-xl p-8 hover:shadow-xl transition-all duration-500 group hover:scale-105 hover:border-cyan-400/30 relative overflow-hidden cursor-pointer"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.8,
          delay: industry.delay,
        }}
        viewport={{ once: true }}
        onClick={handleCardClick}
        data-case-trigger="true"
        data-case-study={industry.id || industry.title}
      >
      <div className="space-y-4">
        <h3 className="text-xl text-foreground group-hover:text-slate-800 transition-colors duration-300">
          {industry.title}
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          {industry.description}
        </p>
        <p className="text-sm text-slate-600 mb-6">
          {industry.industry}
        </p>
        <div className="relative">
          <motion.button
            className="border border-slate-300 text-slate-600 px-6 py-2 rounded-lg hover:border-cyan-400 hover:text-cyan-400 transition-all duration-300 text-sm cursor-pointer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleButtonClick}
          >
            {t('socialMedia.viewCaseStudies')}
          </motion.button>
          {showTooltip && (
            <motion.div
              className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-3 py-1 rounded text-sm whitespace-nowrap z-50"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
            >
              Coming Soon
              <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45"></div>
            </motion.div>
          )}
        </div>
      </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden pt-26">
      <main className="relative">
        {/* Hero Section */}
        <section className="pt-16 pb-20 relative overflow-hidden">
          {/* Animated horizontal line */}
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            {/* Full Width Text Content - No Image */}
            <motion.div
              className="text-center space-y-8 max-w-5xl mx-auto"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                className="space-y-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <h1 className="text-5xl lg:text-7xl leading-tight text-foreground">
                  <span className="block">
                    <span className="bg-gradient-to-r from-slate-800 via-slate-700 to-cyan-600 bg-clip-text text-transparent">
                      {t('socialMedia.heroTitle')}
                    </span>
                  </span>
                </h1>

                <motion.p
                  className="text-xl lg:text-2xl text-muted-foreground leading-relaxed max-w-4xl mx-auto"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  {t('socialMedia.heroDescription')}
                </motion.p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Combined Why & What Section - Hidden for Japanese */}
        {currentLanguage !== 'ja' && (
        <section className="py-20 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Left: Why You Need This Solution */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <motion.h2
                  className="text-2xl lg:text-3xl text-foreground mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  {t('socialMedia.whyThisMatters')}
                </motion.h2>

                <div className="space-y-5">
                  {[
                    { 
                      title: t('socialMedia.whyMattersPoint1Title'),
                      description: t('socialMedia.whyMattersPoint1Desc')
                    },
                    { 
                      title: t('socialMedia.whyMattersPoint2Title'),
                      description: t('socialMedia.whyMattersPoint2Desc')
                    },
                    { 
                      title: t('socialMedia.whyMattersPoint3Title'),
                      description: t('socialMedia.whyMattersPoint3Desc')
                    },
                    { 
                      title: t('socialMedia.whyMattersPoint4Title'),
                      description: t('socialMedia.whyMattersPoint4Desc')
                    }
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.5,
                        delay: 0.2 + index * 0.05,
                      }}
                      viewport={{ once: true }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-gradient-to-br from-cyan-500 to-slate-700 rounded-full mt-2 flex-shrink-0"></div>
                        <div className="flex-1">
                          <h4 className="text-base font-semibold text-foreground mb-1">
                            {item.title}
                          </h4>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Right: What We Offer */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <motion.h2 
                  className="text-2xl lg:text-3xl text-foreground mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  {t('socialMedia.whatWeOffer')}
                </motion.h2>

                <div className="space-y-4">
                  {[
                    {
                      title: t('socialMedia.offerPoint1Title'),
                      description: t('socialMedia.offerPoint1Desc')
                    },
                    {
                      title: t('socialMedia.offerPoint2Title'),
                      description: t('socialMedia.offerPoint2Desc')
                    },
                    {
                      title: t('socialMedia.offerPoint3Title'),
                      description: t('socialMedia.offerPoint3Desc')
                    },
                    {
                      title: t('socialMedia.offerPoint4Title'),
                      description: t('socialMedia.offerPoint4Desc')
                    },
                    {
                      title: t('socialMedia.offerPoint5Title'),
                      description: t('socialMedia.offerPoint5Desc')
                    }
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.5,
                        delay: 0.2 + index * 0.05,
                      }}
                      viewport={{ once: true }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-gradient-to-br from-cyan-500 to-slate-700 rounded-full mt-2 flex-shrink-0"></div>
                        <div className="flex-1">
                          <h4 className="text-base font-semibold text-foreground mb-1">
                            {item.title}
                          </h4>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>
        )}

        {/* SNS & O2O Growth Engine Section - JP Only */}
        {currentLanguage === 'ja' && (
        <section className="py-20 bg-gradient-to-b from-slate-50/30 to-slate-100/10 relative overflow-x-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="text-center space-y-6 mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-5xl text-foreground">
                SNS & O2O Growth Engine (Traffic & Visit)
              </h2>
              <p className="text-lg text-muted-foreground max-w-4xl mx-auto">
                SNSでの認知から興味喚起、そしてオフラインへ足を運ばせるまでの動線を設計
              </p>
            </motion.div>

            {(() => {
              const snsCards = [
                {
                  title: "RedPulse Ops™",
                  image: "/images/tech-market-sl/sl-snso2o-1.jpg",
                  description: "「認知の起点」を作り出し、AI検索（GEO）からの「確認行動」も決定づける SNSアカウント運用",
                  clickable: true,
                  subCards: [
                    {
                      title: "公式SNSアカウント運用",
                      description: "投稿カレンダー設計、企画、中国語コピーライティング、写真デザイン、動画編集、投稿管理まで一括で対応し、「今このブランドが気になる」という状態を継続的に作り"
                    },
                    {
                      title: "KOL/KOCコラボ（UGC造成）",
                      description: "KOL/KOCをリストアップし、選定・交渉・ブリーフ作成・投稿内容チェック等。来店・予約・ECへの送客を前提とした「種まき」を実行"
                    },
                    {
                      title: "SNS内検索最適化",
                      description: "検索ワードに基づき、タイトル・タグ・カバー画像・位置情報を調整し、SNS内検索で見つけられやすい投稿群を設計"
                    }
                  ]
                },
                {
                  title: "Crimson Boost™",
                  image: "/images/tech-market-sl/sl-snso2o-2.jpg",
                  description: "「いいね」ではなく、「来店」にコミットするSNS広告運用ソリューション",
                  clickable: true,
                  subCards: [
                    {
                      title: "SNS広告運用（Redbook／DouYin／DianPing／Meta）",
                      description: "来店・予約をKPIにしたキャンペーンを設計し、ターゲティング・クリエイティブ・入札調整・週次最適化まで運用。KOL投稿の二次利用クリエイティブも含む"
                    },
                    {
                      title: "Baidu Ads＋中国語のLP制作",
                      description: "消費者がBaiduで検索した際、確実に公式サイト／中国語のLP／WeChatへ誘導できるよう、ブランド名＆カテゴリキーワードの検索広告を設計・運用"
                    },
                    {
                      title: "LBS＆リターゲティング設計",
                      description: "店舗周辺・競合店舗周辺への位置情報ターゲティングと、SNS上の行動データを組み合わせ、クーポンや限定オファーで来店・予約を後押"
                    }
                  ]
                },
                {
                  title: "Trend Signal™",
                  image: "/images/tech-market-sl/sl-snso2o-3.jpg",
                  description: "市場トレンドを先読みし、最適なタイミングで施策を打つためのインサイトエンジン",
                  clickable: true,
                  subCards: [
                    {
                      title: "SNS熱量予兆検知",
                      description: "膨大な投稿の中から、特定のキーワードや商品カテゴリーに対する「保存数」「シェア数」の異常値を検知し、次に爆発するトレンドの「火種」を特定"
                    },
                    {
                      title: "ユーザー本音解析",
                      description: "SNSのコメント欄やレビューに隠された、消費者の「未充足の不満」や「潜在的な悩み」を深掘りし、ユーザーに深く刺さる訴求を設計"
                    },
                    {
                      title: "クロスプラットフォーム同期分析",
                      description: "地域を跨いだSNS上のトレンドの時差を利用し、ある国での成功事例を次の国へ「予兆」として持ち込み、連鎖的な成功を創出"
                    }
                  ]
                }
              ];

              const renderSnsParentCard = (card: typeof snsCards[0], index: number) => (
                <motion.div
                  key={index}
                  className={`bg-white rounded-xl overflow-hidden shadow-lg border border-slate-200/60 hover:shadow-xl transition-all duration-300 cursor-pointer ${expandedCaseStudy === card.title ? 'ring-2 ring-orange-500' : ''}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setExpandedCaseStudy(expandedCaseStudy === card.title ? null : card.title)}
                >
                  <div className="bg-orange-500 text-white text-center py-3 relative">
                    <h3 className="text-xl font-semibold">{card.title}</h3>
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80">
                      {expandedCaseStudy === card.title ? '▲' : '▼'}
                    </span>
                  </div>
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={card.image}
                      alt={card.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {card.description}
                    </p>
                  </div>
                </motion.div>
              );

              const renderSnsSubCards = (card: typeof snsCards[0], cardIndex: number, showLines: boolean) => (
                expandedCaseStudy === card.title && card.subCards.length > 0 && (
                  <motion.div
                    key={`sub-${card.title}`}
                    className="relative overflow-visible"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {showLines && (
                      <div className="relative" style={{ height: '4rem', marginTop: '-2rem', marginBottom: '0rem' }}>
                        <div
                          className="absolute w-0.5 bg-orange-400"
                          style={{
                            left: cardIndex === 0 ? 'calc(16.67% - 1px)' : cardIndex === 1 ? 'calc(50% - 1px)' : 'calc(83.33% - 1px)',
                            top: '0',
                            height: '50%'
                          }}
                        />
                        <div
                          className="absolute h-0.5 bg-orange-400"
                          style={{
                            left: 'calc(16.67% - 1px)',
                            width: 'calc(66.66% + 2px)',
                            top: '50%'
                          }}
                        />
                        <div className="absolute w-0.5 bg-orange-400" style={{ left: 'calc(16.67% - 1px)', top: '50%', height: '50%' }} />
                        <div className="absolute w-0.5 bg-orange-400" style={{ left: 'calc(50% - 1px)', top: '50%', height: '50%' }} />
                        <div className="absolute w-0.5 bg-orange-400" style={{ left: 'calc(83.33% - 1px)', top: '50%', height: '50%' }} />
                      </div>
                    )}
                    <div className={showLines ? "grid md:grid-cols-3 gap-6" : "space-y-4"}>
                      {card.subCards.map((subCard, subIndex) => (
                        <motion.div
                          key={subIndex}
                          className="bg-white rounded-xl overflow-hidden shadow-md border-l-4 border-orange-500"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: subIndex * 0.1 }}
                        >
                          <div className="bg-orange-100 text-orange-700 px-4 py-3">
                            <h4 className="font-semibold">{subCard.title}</h4>
                          </div>
                          <div className="p-4">
                            <p className="text-muted-foreground text-sm leading-relaxed">
                              {subCard.description}
                            </p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )
              );

              return (
                <>
                  <div className="hidden md:block space-y-8 overflow-visible">
                    <div className="grid grid-cols-3 gap-8">
                      {snsCards.map((card, index) => renderSnsParentCard(card, index))}
                    </div>
                    <div className="overflow-visible">
                      {snsCards.map((card, cardIndex) => renderSnsSubCards(card, cardIndex, true))}
                    </div>
                  </div>
                  <div className="md:hidden space-y-6">
                    {snsCards.map((card, index) => (
                      <div key={index} className="space-y-4">
                        {renderSnsParentCard(card, index)}
                        {renderSnsSubCards(card, index, false)}
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        </section>
        )}

        {/* Social Commerce & EC Flywheel Section - JP Only */}
        {currentLanguage === 'ja' && (
        <section className="py-20 relative overflow-x-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="text-center space-y-6 mb-12"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-5xl text-foreground">
                Social Commerce & EC Loop
              </h2>
              <p className="text-lg text-muted-foreground max-w-4xl mx-auto">
                オフラインの出会いを、オンラインのLTVへつなげる
              </p>
            </motion.div>

            {(() => {
              const ecCards = [
                {
                  title: "EC Store Setup",
                  image: "/images/tech-market-sl/sl-scec-1.jpg",
                  description: "SNSの文脈をそのまま購買へ繋げる、コンバージョン重視の越境EC店舗構築・運営パッケージ",
                  clickable: true,
                  subCards: [
                    {
                      title: "ECチャネル選定＆立ち上げ支援",
                      description: "TikTok/DouYin Shop、Redbook EC、T-Mall、自社越境ECなどから最適チャネルを選定し、店舗開設・アカウント設定・基本設計を支援"
                    },
                    {
                      title: "商品情報ローカライズ＆ページ制作",
                      description: "商品タイトル・説明文・ベネフィット訴求・画像構成を現地語で最適化し、「SNSで見た世界観のまま買える」商品ページに整える"
                    },
                    {
                      title: "プロモーション＆キャンペーン設計",
                      description: "セール、セット販売、初回限定オファーなどのキャンペーンを設計し、RedPulse Ops / CrimsonBoost との連動（種まき投稿→EC購入）まで含めてプランニング"
                    }
                  ]
                },
                {
                  title: "Live Commerce™",
                  image: "/images/tech-market-sl/sl-scec-2.jpg",
                  description: "KOL/KOCネットワークとライブ配信を活用し、短期間で爆発的な売上を作る販促エンジン",
                  clickable: true,
                  subCards: [
                    {
                      title: "ライブ×O2O／EC連動キャンペーン設計",
                      description: "ライブ視聴者限定クーポン、来店者限定ライブ、ライブ後のSNSレビューキャンペーンなど、オンラインとオフラインをつなぐ施策をセットで設計"
                    },
                    {
                      title: "ライブコマース企画＆シナリオ設計",
                      description: "誰が・どの商品を・どの順番で・どう見せるかを明確にしたライブ企画と、タイムライン付き進行台本（オープニングトーク〜クロージングまで）を作成"
                    },
                    {
                      title: "KOLライブディレクション",
                      description: "適切なKOLの選定、ブリーフ作成、事前リハーサル、当日の進行チェックまでをサポートし、「盛り上がる＋売れる」進行を設計"
                    }
                  ]
                },
                {
                  title: "Loyalty Funnel™",
                  image: "/images/tech-market-sl/sl-scec-3.jpg",
                  description: "一見客を「生涯顧客」に変える、WeChat / Line起点の会員基盤・自動追客システム",
                  clickable: true,
                  subCards: [
                    {
                      title: "会員・フォロワー獲得導線設計",
                      description: "店頭・EC・予約フォームなど、あらゆる接点でWeChat/LINE登録を自然に促せる導線と文言を設計"
                    },
                    {
                      title: "ステップ配信シナリオ設計",
                      description: "初回利用から30日・90日までの期間で、サンクスメッセージ、次回クーポン、新商品案内などの配信シナリオ（現地語メッセージテンプレ含む）を作成"
                    },
                    {
                      title: "レビュー・UGC促進キャンペーン",
                      description: "Redbook・大衆点評・OTAなどへの口コミ投稿キャンペーンを企画し、POP・SNS告知・参加手順まで一式を設計して、再度の種まきに繋げる"
                    }
                  ]
                }
              ];

              const renderEcParentCard = (card: typeof ecCards[0], index: number) => (
                <motion.div
                  key={index}
                  className={`bg-white rounded-xl overflow-hidden shadow-lg border border-slate-200/60 hover:shadow-xl transition-all duration-300 cursor-pointer ${expandedCaseStudy === card.title ? 'ring-2 ring-emerald-500' : ''}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => setExpandedCaseStudy(expandedCaseStudy === card.title ? null : card.title)}
                >
                  <div className="bg-emerald-500 text-white text-center py-3 relative">
                    <h3 className="text-xl font-semibold">{card.title}</h3>
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80">
                      {expandedCaseStudy === card.title ? '▲' : '▼'}
                    </span>
                  </div>
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={card.image}
                      alt={card.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {card.description}
                    </p>
                  </div>
                </motion.div>
              );

              const renderEcSubCards = (card: typeof ecCards[0], cardIndex: number, showLines: boolean) => (
                expandedCaseStudy === card.title && card.subCards.length > 0 && (
                  <motion.div
                    key={`sub-${card.title}`}
                    className="relative overflow-visible"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {showLines && (
                      <div className="relative" style={{ height: '4rem', marginTop: '-2rem', marginBottom: '0rem' }}>
                        <div
                          className="absolute w-0.5 bg-emerald-400"
                          style={{
                            left: cardIndex === 0 ? 'calc(16.67% - 1px)' : cardIndex === 1 ? 'calc(50% - 1px)' : 'calc(83.33% - 1px)',
                            top: '0',
                            height: '50%'
                          }}
                        />
                        <div
                          className="absolute h-0.5 bg-emerald-400"
                          style={{
                            left: 'calc(16.67% - 1px)',
                            width: 'calc(66.66% + 2px)',
                            top: '50%'
                          }}
                        />
                        <div className="absolute w-0.5 bg-emerald-400" style={{ left: 'calc(16.67% - 1px)', top: '50%', height: '50%' }} />
                        <div className="absolute w-0.5 bg-emerald-400" style={{ left: 'calc(50% - 1px)', top: '50%', height: '50%' }} />
                        <div className="absolute w-0.5 bg-emerald-400" style={{ left: 'calc(83.33% - 1px)', top: '50%', height: '50%' }} />
                      </div>
                    )}
                    <div className={showLines ? "grid md:grid-cols-3 gap-6" : "space-y-4"}>
                      {card.subCards.map((subCard, subIndex) => (
                        <motion.div
                          key={subIndex}
                          className="bg-white rounded-xl overflow-hidden shadow-md border-l-4 border-emerald-500"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: subIndex * 0.1 }}
                        >
                          <div className="bg-emerald-100 text-emerald-700 px-4 py-3">
                            <h4 className="font-semibold">{subCard.title}</h4>
                          </div>
                          <div className="p-4">
                            <p className="text-muted-foreground text-sm leading-relaxed">
                              {subCard.description}
                            </p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )
              );

              return (
                <>
                  <div className="hidden md:block space-y-8 overflow-visible">
                    <div className="grid grid-cols-3 gap-8">
                      {ecCards.map((card, index) => renderEcParentCard(card, index))}
                    </div>
                    <div className="overflow-visible">
                      {ecCards.map((card, cardIndex) => renderEcSubCards(card, cardIndex, true))}
                    </div>
                  </div>
                  <div className="md:hidden space-y-6">
                    {ecCards.map((card, index) => (
                      <div key={index} className="space-y-4">
                        {renderEcParentCard(card, index)}
                        {renderEcSubCards(card, index, false)}
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>
        </section>
        )}

        {/* Online Marketing - Industry Case Studies */}
        <section className="py-20 bg-gradient-to-b from-slate-50/30 to-slate-100/10 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              className="text-center space-y-6 mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-5xl text-foreground">
                {t('socialMedia.onlineSocialMediaTitle').split('\n').map((line, i, arr) => (
                  <span key={i}>
                    {line}
                    {i < arr.length - 1 && <br />}
                  </span>
                ))}
              </h2>
              <p className="text-lg text-muted-foreground">
                {t('socialMedia.industrySubtitle')}
              </p>
            </motion.div>

            {/* Industry Cards with Smart Layout */}
            <div className="space-y-8">
              {/* Render industry cards with dynamic layout */}
              {(() => {
                const industries = [
                  {
                    id: "Food & Beverage",
                    title: t('socialMedia.industryFoodTitle'),
                    description: t('socialMedia.industryFoodDesc'),
                    industry: "",
                    delay: 0,
                    accent: "cyan",
                  },
                  {
                    id: "Hotels & Tourism",
                    title: t('socialMedia.industryHotelsTitle'),
                    description: t('socialMedia.industryHotelsDesc'),
                    industry: "", 
                    delay: 0.1,
                    accent: "slate",
                  },
                  {
                    id: "Retail",
                    title: t('socialMedia.industryRetailTitle'),
                    description: t('socialMedia.industryRetailDesc'),
                    industry: "",
                    delay: 0.2,
                    accent: "cyan",
                  },
                  {
                    id: "Beauty & Skincare",
                    title: t('socialMedia.industryBeautyTitle'),
                    description: t('socialMedia.industryBeautyDesc'),
                    industry: "",
                    delay: 0.3,
                    accent: "slate",
                  },
                  {
                    id: "Fashion",
                    title: t('socialMedia.industryFashionTitle'),
                    description: t('socialMedia.industryFashionDesc'),
                    industry: "",
                    delay: 0.4,
                    accent: "cyan",
                  },
                  {
                    id: "Spa & Wellness",
                    title: t('socialMedia.industrySpaTitle'),
                    description: t('socialMedia.industrySpaDesc'),
                    industry: "",
                    delay: 0.5,
                    accent: "slate",
                  },
                ];

                // Check if any of the industry case studies are expanded
                const industryExpanded = industries.some(ind => ind.id === expandedCaseStudy);
                
                if (!industryExpanded) {
                  // Normal layout - 3 in first row, 3 in second row
                  return (
                    <>
                      <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-8">
                        {industries.slice(0, 3).map((industry, index) => (
                          <IndustryCard key={index} industry={industry} onExpand={handleCaseStudyClick} />
                        ))}
                      </div>
                      <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-8">
                        {industries.slice(3, 6).map((industry, index) => (
                          <IndustryCard key={index + 3} industry={industry} onExpand={handleCaseStudyClick} />
                        ))}
                      </div>
                    </>
                  );
                }

                // Find the expanded industry index
                const expandedIndex = industries.findIndex(ind => ind.id === expandedCaseStudy);
                
                // Rearrange industries for expanded state
                const beforeExpanded = industries.slice(0, expandedIndex);
                const afterExpanded = industries.slice(expandedIndex + 1);
                
                return (
                  <>
                    {/* Industries before the expanded one */}
                    {beforeExpanded.length > 0 && (
                      <div className={`grid gap-8 ${beforeExpanded.length === 1 ? 'lg:grid-cols-1 max-w-md mx-auto' : beforeExpanded.length === 2 ? 'lg:grid-cols-2 max-w-2xl mx-auto' : 'lg:grid-cols-3'} md:grid-cols-2 grid-cols-1`}>
                        {beforeExpanded.map((industry, index) => (
                          <IndustryCard key={`before-${index}`} industry={industry} onExpand={handleCaseStudyClick} />
                        ))}
                      </div>
                    )}
                    
                    {/* Expanded case study content - only for industry case studies */}
                    {renderIndustryExpandedContent()}
                    
                    {/* Industries after the expanded one */}
                    {afterExpanded.length > 0 && (
                      <div className={`grid gap-8 ${afterExpanded.length === 1 ? 'lg:grid-cols-1 max-w-md mx-auto' : afterExpanded.length === 2 ? 'lg:grid-cols-2 max-w-2xl mx-auto' : afterExpanded.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} md:grid-cols-2 grid-cols-1`}>
                        {afterExpanded.map((industry, index) => (
                          <IndustryCard key={`after-${index}`} industry={industry} onExpand={handleCaseStudyClick} />
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </section>

        {/* Offline Support Section - moved before KOL/KOC - Hidden for Japanese */}
        {currentLanguage !== 'ja' && (
        <section className="py-20 bg-gradient-to-b from-slate-50/30 to-slate-100/10 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            {/* Full Width Content - No Image */}
            <motion.div
              className="space-y-8 max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2
                className="text-3xl lg:text-5xl text-foreground text-center"
                dangerouslySetInnerHTML={{ __html: t('socialMedia.offlineChineseFriendlyTitle') }}
              />
              <p className="text-lg text-muted-foreground leading-relaxed text-center">
                {t('socialMedia.offlineDescription')}
              </p>

              <div className="space-y-4 max-w-3xl mx-auto">
                {[
                  t('socialMedia.offlinePoint1'),
                  t('socialMedia.offlinePoint2'),
                  t('socialMedia.offlinePoint3'),
                  t('socialMedia.offlinePoint4'),
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start space-x-4 group"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.1,
                    }}
                    viewport={{ once: true }}
                  >
                    <motion.div
                      className="w-6 h-6 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-full mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-300 relative overflow-hidden"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-tr from-transparent to-white/30"></div>
                      <span className="absolute inset-0 flex items-center justify-center text-white text-xs">
                        ✓
                      </span>
                    </motion.div>
                    <span className="text-foreground leading-relaxed">
                      {item}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Support Expanded Content - only render for Chinese-Friendly Support */}
            {renderSupportExpandedContent()}
          </div>
        </section>
        )}

        {/* KOL/KOC Marketing Section - moved after Offline Support - Hidden for Japanese */}
        {currentLanguage !== 'ja' && (
        <section id="kol-marketing" className="py-20 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              className="text-center space-y-4 mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-5xl text-foreground">
                {t('socialMedia.onlineKolTitle')}
              </h2>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-16">
              {/* Left Content - Existing content */}
              <motion.div
                className="space-y-6"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl lg:text-2xl text-foreground font-semibold">
                  {t('socialMedia.kolWhyYouNeed')}
                </h3>
                
                <div className="space-y-4">
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    {t('socialMedia.kolIntro1')}
                  </p>
                  
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    {t('socialMedia.kolIntro2')}
                  </p>

                  {[
                    t('socialMedia.kolPoint1'),
                    t('socialMedia.kolPoint2'),
                    t('socialMedia.kolPoint3'),
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      className="flex items-start space-x-4 group"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.5,
                        delay: index * 0.1,
                      }}
                      viewport={{ once: true }}
                    >
                      <div className="w-2 h-2 bg-gradient-to-br from-cyan-500 to-slate-700 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-foreground leading-relaxed">
                        {item}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Right Content - What We Offer */}
              <motion.div
                className="space-y-6"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h3 className="text-xl lg:text-2xl text-foreground font-semibold">
                  {t('socialMedia.kolWhatWeOffer')}
                </h3>
                <div className="space-y-4">
                  {[
                    {
                      title: t('socialMedia.kolOfferPoint1Title'),
                      description: t('socialMedia.kolOfferPoint1Desc')
                    },
                    {
                      title: t('socialMedia.kolOfferPoint2Title'),
                      description: t('socialMedia.kolOfferPoint2Desc')
                    },
                    {
                      title: t('socialMedia.kolOfferPoint3Title'),
                      description: t('socialMedia.kolOfferPoint3Desc')
                    },
                    {
                      title: t('socialMedia.kolOfferPoint4Title'),
                      description: t('socialMedia.kolOfferPoint4Desc')
                    },
                    {
                      title: t('socialMedia.kolOfferPoint5Title'),
                      description: t('socialMedia.kolOfferPoint5Desc')
                    }
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      className="flex items-start space-x-4 group"
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.5,
                        delay: index * 0.1,
                      }}
                      viewport={{ once: true }}
                    >
                      <div className="w-2 h-2 bg-gradient-to-br from-cyan-500 to-slate-700 rounded-full mt-2 flex-shrink-0"></div>
                      <div className="flex-1">
                        <h4 className="text-base font-semibold text-foreground mb-1">
                          {item.title}
                        </h4>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Centered View Case Studies Button */}
            <motion.div
              className="text-center mt-12 relative"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <motion.button
                className="border border-slate-300 text-slate-600 px-8 py-3 rounded-lg hover:border-cyan-400 hover:text-cyan-400 transition-all duration-300 cursor-pointer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  handleCaseStudyClick("KOL/KOC Marketing");
                }}
                data-case-trigger="true"
                data-case-study="KOL/KOC Marketing"
              >
                {expandedCaseStudy === "KOL/KOC Marketing" ? t('socialMedia.hideCaseStudies') : t('socialMedia.viewCaseStudies')}
              </motion.button>
              {showComingSoon && (
                <motion.div
                  className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded-lg text-sm whitespace-nowrap z-50"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  Coming Soon
                  <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45"></div>
                </motion.div>
              )}
            </motion.div>

            {/* KOL Expanded Content - only render for KOL/KOC Marketing */}
            {renderKOLExpandedContent()}
          </div>
        </section>
        )}

        {/* Service Process Section */}
        <section className="py-20 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="text-left space-y-6 mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-5xl text-foreground">
                {t('socialMedia.serviceProcessTitle')}
              </h2>
              <p className="text-lg text-muted-foreground">
                {t('socialMedia.serviceProcessSubtitle')}
              </p>
            </motion.div>

            <div className="flex flex-col gap-6">
              {[
                {
                  step: "1",
                  title: t('socialMedia.processStep1Title'),
                  description: t('socialMedia.processStep1Desc'),
                },
                {
                  step: "2", 
                  title: t('socialMedia.processStep2Title'),
                  description: t('socialMedia.processStep2Desc'),
                },
                {
                  step: "3",
                  title: t('socialMedia.processStep3Title'),
                  description: t('socialMedia.processStep3Desc'), 
                },
                {
                  step: "4",
                  title: t('socialMedia.processStep4Title'), 
                  description: t('socialMedia.processStep4Desc'),
                },
                {
                  step: "5",
                  title: t('socialMedia.processStep5Title'),
                  description: t('socialMedia.processStep5Desc'),
                },
                {
                  step: "6",
                  title: t('socialMedia.processStep6Title'),
                  description: t('socialMedia.processStep6Desc'),
                },
                {
                  step: "7",
                  title: t('socialMedia.processStep7Title'),
                  description: t('socialMedia.processStep7Desc'),
                },
              ].map((process, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-4 group"
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{
                    duration: 0.8,
                    delay: index * 0.1,
                  }}
                  viewport={{ once: true }}
                >
                  <motion.div
                    className="w-12 h-12 bg-gradient-to-br from-slate-800 to-slate-700 rounded-full flex-shrink-0 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 relative overflow-hidden"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.8 }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-tr from-transparent to-cyan-400/30"></div>
                    <span className="text-white text-base font-semibold relative z-10">
                      {process.step}
                    </span>
                  </motion.div>
                  
                  <div className="flex-1 pt-1">
                    <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-slate-800 transition-colors duration-300">
                      {process.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {process.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}